import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  NgZone,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { environment } from '@env/environment';
import { Button } from '@shared/components';
import { BreakpointService } from '@shared/services';

import type { NavItem } from '../../models';

/** Distance scrolled before the header hides. */
const SCROLL_THRESHOLD = 100;

/** Minimum height of the strip at the top of the viewport that brings the header back on hover. */
const MIN_REVEAL_ZONE = 50;

@Component({
  selector: 'app-header',
  imports: [RouterLink, Button],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  host: {
    '[class.is-hidden]': 'hidden()',
  },
})
export class Header {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly hostEl = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private readonly zone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpoints = inject(BreakpointService);

  protected readonly menuOpen = signal(false);
  protected readonly scrolled = signal(false);

  /** Pointer sits in the reveal zone (or, without hover support, the user scrolled back up). */
  private readonly revealed = signal(false);

  protected readonly hidden = computed(
    () => this.scrolled() && !this.revealed() && !this.menuOpen(),
  );

  private lastScrollY = 0;
  private readonly hoverCapable = this.isBrowser && window.matchMedia('(hover: hover)').matches;

  protected readonly environment = environment;

  protected readonly navItems: readonly NavItem[] = [
    { label: 'Product', type: 'internal', routerLink: '/', fragment: 'product' },
    { label: 'Demo', type: 'internal', routerLink: '/demo' },
    { label: 'Plans', type: 'internal', routerLink: '/plans' },
    { label: 'Origin', type: 'internal', routerLink: '/origin' },
    {
      label: 'Contact',
      type: 'internal',
      routerLink: '/contact',
    },
  ];

  @HostListener('window:scroll')
  onWindowScroll(): void {
    const scrollY = window.scrollY;

    this.scrolled.set(scrollY >= SCROLL_THRESHOLD);

    // Touch devices cannot hover, so scrolling back up brings the header back instead.
    if (!this.hoverCapable) {
      this.revealed.set(scrollY < this.lastScrollY);
    }

    this.lastScrollY = scrollY;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (!this.breakpoints.isMobile() && this.menuOpen()) {
      this.menuOpen.set(false);
    }
  }

  constructor() {
    if (this.isBrowser) {
      effect(() => {
        document.body.style.overflow = this.menuOpen() ? 'hidden' : '';
      });

      this.lastScrollY = window.scrollY;
      this.scrolled.set(window.scrollY >= SCROLL_THRESHOLD);

      if (this.hoverCapable) {
        this.trackPointer();
      }
    }
  }

  /**
   * Reveals the header while the pointer is within the top strip of the viewport. The listener
   * runs outside Angular; the signal write is what schedules change detection.
   */
  private trackPointer(): void {
    this.zone.runOutsideAngular(() => {
      const onPointerMove = ({ clientY }: MouseEvent): void => {
        // The - 1 is need to make sure that mouse enters the header
        const revealZone = Math.max(MIN_REVEAL_ZONE, this.hostEl.offsetHeight - 1);

        this.revealed.set(clientY <= revealZone);
      };

      window.addEventListener('mousemove', onPointerMove, { passive: true });

      this.destroyRef.onDestroy(() => window.removeEventListener('mousemove', onPointerMove));
    });
  }

  /**
   * `mousemove` does not cross iframe boundaries (the YouTube embed swallows it), so the reveal
   * zone is also a real element layered above the page: entering it reveals the header, and
   * leaving the header hides it again. Both fire even when the pointer comes from / goes to an
   * iframe.
   */
  setRevealed(revealed: boolean): void {
    this.revealed.set(revealed);
  }

  toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }
}
